'use client';
import 'swiper/css';
import AnimateLines from '@components/animate-lines/AnimateLines';
import ParallaxMedia from '@components/parallax-media/ParallaxMedia';
import SVGelem from '@components/SVGelem/SVGelem';
import { UNIVERSE_DESKTOP_ADD_CONTROLS_ARROWS_FORMA_34211 } from '@lib/flags.js';
import { changeActiveSlide } from '@lib/store/slices/ProjectSlice';
import { SafeHtmlRenderer } from '@lib/tools/helpers/SafeHtmlRenderer';
import { useViewport } from '@lib/tools/hooks/useViewport';
import { showAnimate } from '@lib/tools/utils';
import { animated, useSpring } from '@react-spring/web';
import classNames from 'classnames';
import gsap from 'gsap/all';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { A11y, EffectCreative, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import style from './UniverseInMoments.module.scss';

gsap.registerPlugin(ScrollTrigger);

function UniverseInMoment() {
  const [st7, setSt7] = useState(null);

  const [toggle, setToggle] = useState(false);
  const [toggle2, setToggle2] = useState(false);

  const [activeSlide, setActiveSlide] = useState(null);
  const [visibleNext, setVisibleNext] = useState(false);

  const cursorNext = useRef(null);
  const wrapperNextRef = useRef(null);

  const svgRef = useRef(null);
  const titleRef = useRef(null);
  const descriptionRef = useRef(null);

  const dispatch = useDispatch();

  const slider = useSelector(state => state.projectinfo.slider);

  const { isMobile, isDesktop, isTablet, viewport, userAgent } = useViewport();

  const isDesktopOnly = isDesktop && userAgent !== 'Android' && userAgent !== 'iPad';
  const isTabletOrSpecialDesktop = isTablet || (isDesktop && (userAgent === 'Android' || userAgent === 'iPad'));

  const opacityStyle = useSpring({
    config: {
      duration: 1000,
    },
    form: {
      opacity: 0,
    },
    opacity: !toggle2 ? 1 : 0,
    delay: 0,
  });

  useLayoutEffect(() => {
    if (svgRef && titleRef && descriptionRef) {
      const element = svgRef.current;

      const createScrollTrigger = startParam => {
        const scroller = !isMobile ? document.querySelector('#smooth-wrapper') || document.body : document.body;

        return ScrollTrigger.create({
          trigger: element,
          start: startParam,
          scroller,
          scrub: true,
          pin: false,
          onToggle: () => {
            setTimeout(() => {
              setToggle(() => true);
            }, 500);
          },
        });
      };

      setTimeout(() => {
        if (!isMobile) setSt7(createScrollTrigger('center center'));
        else setSt7(createScrollTrigger('top top+=100px'));

        const addWillShowClass = ref => {
          if (ref.current) {
            [...ref.current.children].forEach(item => {
              item.classList.add('will-show');
            });
          }
        };

        addWillShowClass(titleRef);
        addWillShowClass(descriptionRef);

        showAnimate(titleRef, titleRef.current);
        showAnimate(descriptionRef, titleRef.current);
      }, 1000);
    }
  }, [svgRef, titleRef, descriptionRef, viewport, userAgent]);

  useEffect(() => {
    return () => {
      if (st7) st7.kill();
      setSt7(null);
    };
  }, []);

  useLayoutEffect(() => {
    if (slider) setActiveSlide(slider.filter(item => item.active === true));
  }, [slider]);

  const active = activeSlide?.[0] ?? null;
  const totalSlides = slider?.length ?? 0;

  const moveCursorNext = useCallback(e => {
    if (UNIVERSE_DESKTOP_ADD_CONTROLS_ARROWS_FORMA_34211) return;
    if (cursorNext) {
      setVisibleNext(true);
      if (cursorNext.current) cursorNext.current.style.transform = `translate(${e.pageX}px, ${e.pageY - 40}px)`;
    } else {
      setVisibleNext(false);
    }
  }, []);

  useEffect(() => {
    if (activeSlide && titleRef && descriptionRef) {
      setToggle2(() => true);

      if (
        titleRef?.current?.children[0]?.classList.contains('will-show-animate') ||
        titleRef?.current?.children[1]?.classList.contains('will-show-animate')
      ) {
        [...titleRef.current.children].forEach(item => {
          item.classList.remove('will-show');
          item.classList.remove('will-show-animate');
          return null;
        });
      }
      if (
        descriptionRef?.current?.children[0]?.classList.contains('will-show-animate') ||
        descriptionRef?.current?.children[1]?.classList.contains('will-show-animate')
      ) {
        [...descriptionRef.current.children].forEach(item => {
          item.classList.remove('will-show');
          item.classList.remove('will-show-animate');
          return null;
        });
      }

      setTimeout(() => {
        setToggle2(() => false);
      }, 1000);
    }
  }, [activeSlide, titleRef, descriptionRef]);

  return (
    <div>
      {!UNIVERSE_DESKTOP_ADD_CONTROLS_ARROWS_FORMA_34211 && visibleNext && isDesktopOnly && (
        <button type='button' className={style.swiperButtonNext} ref={cursorNext}>
          <SVGelem className={style.swiperButtonIconNext} href='#arrow' />
        </button>
      )}
      <div data-scroll className={style.eighthScreenContainer} ref={svgRef} id='improvement'>
        <div className={style.eighthScreenSvg}>
          {viewport && (
            <AnimateLines id={!isMobile ? 'vector_4' : 'vector_3_mob'} className={style.svgClass} toggle={toggle} />
          )}
        </div>

        {!UNIVERSE_DESKTOP_ADD_CONTROLS_ARROWS_FORMA_34211 && isDesktopOnly && (
          <div className={style.buttonContainer}>
            <div
              className={classNames(style.buttonWrapperNext, 'buttonWrapperNext')}
              ref={wrapperNextRef}
              onMouseOut={() => setVisibleNext(false)}
              onMouseMove={moveCursorNext}
              onBlur={() => null}
            />
          </div>
        )}

        <div className={style.eighthScreenWrapper}>
          <div className={style.eighthScreenTitle} ref={titleRef}>
            <p>Многомерная вселенная</p>
            <p>для жизни в моменте</p>
          </div>

          {active && (
            <div className={style.eighthScreenBoxDescription} ref={descriptionRef}>
              {!toggle2 && (
                <>
                  <animated.div style={{ ...opacityStyle }} className={style.eighthScreenSubTitle}>
                    {active?.subTitle}
                  </animated.div>

                  <animated.div style={opacityStyle}>
                    <SafeHtmlRenderer
                      tag='div'
                      content={active?.description || ''}
                      className={style.eighthScreenDescription}
                    />
                  </animated.div>
                </>
              )}
            </div>
          )}

          {(isMobile || isTabletOrSpecialDesktop) && active && (
            <div className={style.buttonContainerMobile}>
              <button type='button' className={classNames(style.swiperButtonPrevMobile, 'swiperButtonPrevMobile')}>
                <SVGelem className={style.swiperButtonIconPrevMobile} href='#arrow-small-mob' />
              </button>
              <div className={style.swiperPaginationMobile}>
                0{(active?.id ?? 0) + 1}/0{totalSlides}
              </div>
              <button type='button' className={classNames(style.swiperButtonNextMobile2, 'swiperButtonNextMobile')}>
                <SVGelem className={style.swiperButtonIconNextMobile} href='#arrow-small-mob' />
              </button>
            </div>
          )}

          {UNIVERSE_DESKTOP_ADD_CONTROLS_ARROWS_FORMA_34211 && isDesktopOnly && active && (
            <div className={style.controlsDesktop}>
              <button type='button' className={classNames(style.swiperButtonPrevDesktop, 'swiperButtonPrevDesktop')}>
                <SVGelem className={style.swiperButtonIconPrevDesktop} href='#arrow' />
              </button>
              <div className={style.swiperPaginationDesktopInline}>
                0{(active?.id ?? 0) + 1}/0{totalSlides}
              </div>
              <button type='button' className={classNames(style.swiperButtonNextDesktop, 'swiperButtonNextDesktop')}>
                <SVGelem className={style.swiperButtonIconNextDesktop} href='#arrow' />
              </button>
            </div>
          )}

          {isDesktopOnly && active && (
            <Swiper
              modules={[Navigation, Pagination, A11y, EffectCreative]}
              effect='creative'
              loop
              creativeEffect={{
                prev: { shadow: true, translate: ['-20%', 0, -1] },
                next: { translate: ['100%', 0, 0] },
              }}
              spaceBetween={0}
              slidesPerView={1}
              longSwipesMs={800}
              speed={800}
              onSlideChange={swiper => {
                setToggle2(() => true);
                dispatch(changeActiveSlide(swiper.realIndex));
              }}
              navigation={
                UNIVERSE_DESKTOP_ADD_CONTROLS_ARROWS_FORMA_34211
                  ? { nextEl: '.swiperButtonNextDesktop', prevEl: '.swiperButtonPrevDesktop' }
                  : { nextEl: '.buttonWrapperNext', prevEl: '.buttonWrapperPrev' }
              }
              className={style.eighthScreenImageBox}
            >
              {slider?.map(slide => (
                <SwiperSlide key={slide.id} index={slide.id} className={style.swiperSlide}>
                  <ParallaxMedia
                    onMove
                    height='100%'
                    src={slide.src}
                    name={slide.title}
                    imagePlug={slide.plug}
                    className={style.swiperSlideImg}
                  />
                </SwiperSlide>
              ))}
              {!UNIVERSE_DESKTOP_ADD_CONTROLS_ARROWS_FORMA_34211 && (
                <div className={style.swiperPaginationDesktop}>
                  0{(active?.id ?? 0) + 1}/0{totalSlides}
                </div>
              )}
            </Swiper>
          )}

          {isTabletOrSpecialDesktop && activeSlide && (
            <Swiper
              modules={[Navigation, Pagination, A11y, EffectCreative]}
              effect='creative'
              loop
              longSwipesMs={800}
              speed={800}
              creativeEffect={{
                prev: { shadow: true, translate: ['-20%', 0, -1] },
                next: { translate: ['100%', 0, 0] },
              }}
              spaceBetween={10}
              slidesPerView={1}
              onSlideChange={swiper => {
                setToggle2(() => true);
                dispatch(changeActiveSlide(swiper.realIndex));
              }}
              navigation={{ nextEl: '.swiperButtonNextMobile', prevEl: '.swiperButtonPrevMobile' }}
              className={style.eighthScreenImageBox}
            >
              {slider?.map(slide => (
                <SwiperSlide key={slide.id} index={slide.id} className={style.swiperSlide}>
                  <ParallaxMedia
                    onMove
                    height='100%'
                    name={slide.title}
                    imagePlug={slide.plug}
                    className={style.swiperSlideImg}
                    src={slide.src}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          )}

          {isMobile && activeSlide && (
            <Swiper
              modules={[Navigation, Pagination, A11y, EffectCreative]}
              loop
              effect='creative'
              longSwipesMs={800}
              speed={800}
              creativeEffect={{
                prev: { shadow: true, translate: ['-20%', 0, -1] },
                next: { translate: ['100%', 0, 0] },
              }}
              spaceBetween={10}
              slidesPerView={1}
              onSlideChange={swiper => {
                setToggle2(() => true);
                dispatch(changeActiveSlide(swiper.realIndex));
              }}
              navigation={{ nextEl: '.swiperButtonNextMobile', prevEl: '.swiperButtonPrevMobile' }}
              className={style.eighthScreenImageBox}
            >
              {slider?.map(slide => (
                <SwiperSlide key={slide.id} index={slide.id} className={style.swiperSlide}>
                  <ParallaxMedia
                    onMove
                    name={slide.title}
                    height='100%'
                    src375={slide.srcMob}
                    imagePlug={slide.plug}
                    className={style.swiperSlideImg}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </div>
      </div>
    </div>
  );
}

export default UniverseInMoment;
